/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import axios from 'axios';
import { makeApiPath } from '../utils/apiUtils';
import logger from '../utils/logger';

// Topic and explanation interfaces
export interface ITopicExplanation {
  text: string;
  qualityScore: number;
  providedBy: string;
  debateId: string;
  addedAt: Date;
  
  // Enhanced CUBE fields
  status?: 'active' | 'challenged' | 'under_challenge';
  version?: number;
  challengeHistory?: Array<{
    challengeId: string;
    challengerId: string;
    challengeDate: Date;
    status: 'pending' | 'ai_defending' | 'human_defending' | 'failed' | 'successful';
    challengeText: string;
    result?: 'challenge_failed' | 'challenge_successful' | 'explanation_updated';
    resultReason?: string;
  }>;
  openCriticisms?: Array<{
    criticismText: string;
    sourceDebateId: string;
    raisedBy: string;
    raisedAt: Date;
    type: 'empirical' | 'logical' | 'scope' | 'other';
    addressed: boolean;
  }>;
  challengesSurvived?: number;
  challengesReceived?: number;
  contributors?: string[];
  lastChallengedAt?: Date;
  lastUpdatedAt?: Date;
}

export interface ITopic {
  _id: string;
  name: string;
  description?: string;
  parentTopic?: string;
  childTopics: string[];
  explanations: ITopicExplanation[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Get all topics, optionally filtered by parent topic or search query
 * @param parentTopic Optional parent topic ID to filter by
 * @param searchQuery Optional search query to filter topics by name
 * @returns Promise resolving to array of topics
 */
export const getAllTopics = async (
  parentTopic?: string, 
  searchQuery?: string
): Promise<ITopic[]> => {
  try {
    let url = makeApiPath('/topics');
    
    // Add query parameters if provided
    const params = new URLSearchParams();
    if (parentTopic) params.append('parentTopic', parentTopic);
    if (searchQuery) params.append('search', searchQuery);
    
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    
    const response = await axios.get(url);
    return response.data.topics;
  } catch (error) {
    logger.error('Error fetching topics:', error);
    throw error;
  }
};

/**
 * Get a topic by ID
 * @param id Topic ID
 * @returns Promise resolving to a single topic
 */
export const getTopicById = async (id: string): Promise<ITopic> => {
  try {
    const response = await axios.get(makeApiPath(`/topics/${id}`));
    return response.data.topic;
  } catch (error) {
    logger.error(`Error fetching topic ${id}:`, error);
    throw error;
  }
};

/**
 * Get explanations for a topic
 * @param id Topic ID
 * @returns Promise resolving to array of explanations
 */
export const getTopicExplanations = async (id: string): Promise<ITopicExplanation[]> => {
  try {
    const response = await axios.get(makeApiPath(`/topics/${id}/explanations`));
    return response.data.explanations;
  } catch (error) {
    logger.error(`Error fetching explanations for topic ${id}:`, error);
    throw error;
  }
};

/**
 * Get the debate details for a given debate ID
 * @param debateId Debate ID
 * @returns Promise resolving to basic debate info
 */
export const getDebateInfo = async (debateId: string): Promise<{ 
  id: string;
  topic: string;
  url: string;
}> => {
  try {
    const response = await axios.get(makeApiPath(`/debates/${debateId}`));
    return {
      id: response.data._id,
      topic: response.data.topic,
      url: `/debate/view/${debateId}`
    };
  } catch (error) {
    logger.error(`Error fetching debate info for ${debateId}:`, error);
    // Return a minimal object if the debate can't be found
    return {
      id: debateId,
      topic: 'Unknown debate',
      url: `/debate/view/${debateId}`
    };
  }
};

/**
 * Search topics by query string
 * @param query Search query
 * @returns Promise resolving to array of matching topics
 */
export const searchTopics = async (query: string): Promise<ITopic[]> => {
  try {
    const response = await axios.get(makeApiPath(`/topics?search=${encodeURIComponent(query)}`));
    return response.data.topics;
  } catch (error) {
    logger.error(`Error searching topics with query "${query}":`, error);
    throw error;
  }
}; 